import React from 'react';
import { Box, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
import { useHistory } from 'react-router';

import { EquipmentRepairOrder } from '@app/api/models/equipment';
import { entityBaseBodyRender, TableColumn } from '@app/components/common/table';

import { userBodyRender } from '../common/table/body-renders/user';
import { dateTimeBodyRender } from '../common/table/body-renders/date-time';
import { getDateTimeSelectFilter } from '../common/table/filter/get-datetime-select-filter';
import { SendModal } from './modals/send';
import { CancelModal } from './modals/remove';
import { EquipmentsRepairFormInstance } from './types';
import { Txt } from '../common';
import { CheckStatusEditModal } from './modals/check-status-edit';

export type ColumnsOptions = {
  type: EntityBase[];
};

export const useColumns = ({ canCheckStatusEdit }: {
  canCheckStatusEdit: boolean;
}) => {
  const history = useHistory();

  const columns: TableColumn<EquipmentRepairOrder>[] = [
    {
      name: 'id',
      label: '#',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'visit_dt',
      label: 'Дата/время',
      options: {
        filter: false,
        sort: true,
        ...getDateTimeSelectFilter('visit_dt', 'Дата/время'),
        customBodyRender: dateTimeBodyRender(),
      },
    },
    {
      name: 'service_data',
      label: 'Модель',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (service_data) => (
          <Box whiteSpace='pre-wrap'>
            {service_data?.manufacturer?.name || ''}{' '}
            {service_data?.model?.name || '-'}
          </Box>
        ),
      },
    },
    {
      name: 'service_data',
      label: 'Серийный номер',
      options: {
        filter: {
          name: 'serial_num',
        },
        sort: false,
        customBodyRender: (service_data) => (
          <>{service_data?.serial_num || '-'}</>
        ),
      },
    },
    {
      name: 'service_data',
      label: 'Инвентарный номер',
      options: {
        filter: {
          name: 'inventory_num'
        },
        sort: false,
        customBodyRender: (service_data) => (
          <>{service_data?.inventory_num || '-'}</>
        ),
      },
    },
    {
      name: 'service_data',
      label: 'Торговая точка',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (service_data) => (
          <Flex flexDirection="column">
            <Tooltip label={service_data?.full_address || ''}>
              <Flex gap={2}>
                {!!service_data.outlet?.id && <Txt color='gray.500'>[{service_data?.outlet?.id}]</Txt> } 
                <Box whiteSpace='pre-wrap'>
                  {service_data?.outlet?.name ||
                    service_data?.outlet.outlet_name ||
                    '-'}
                </Box>
              </Flex>
            </Tooltip>
          </Flex>
        ),
      },
    },
    {
      name: 'service_data',
      label: 'Тип сервиса',
      options: {
        filter: {
          name: 'problem_id',
          sourceName: 'service_breakdown',
          type: 'multi-select',
        },
        sort: false,
        customBodyRender: (service_data) => (
          <>{service_data?.problem?.name || '-'}</>
        ),
      },
    },
    {
      name: 'service_data',
      label: 'Комментарий',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (service_data) => <Box whiteSpace='pre-wrap'>{service_data?.comment || '-'}</Box>,
      },
    },
    {
      name: 'contacts',
      label: 'Контакт',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (contacts) => <Box whiteSpace="pre-wrap">{contacts || '-'}</Box>,
      },
    },
    {
      name: 'user',
      label: 'Пользователь',
      options: {
        filter: true,
        sort: true,
        customBodyRender: userBodyRender(),
      },
    },
    {
      name: 'department',
      label: 'Сервисная организация',
      options: {
        filter: false,
        sort: false,
        customBodyRender: entityBaseBodyRender(),
      },
    },
    {
      name: 'coordinator_comment',
      label: 'Комментарий координатора',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: string, { rowData }) =>  <Box whiteSpace="pre-wrap">{value}</Box>,
      },
    },
    {
      name: 'status',
      label: 'Статус',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: EntityBase, { rowData }) =>  <Flex gap={2} alignItems="center">
          {entityBaseBodyRender()(value)}  
          {/* {value.id !== 4 && <StatusEditModal id={rowData.id} currentStatus={value} />} */}
        </Flex>,
      },
    },
    {
      name: 'checked_status',
      label: 'Статус проверки',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: EntityBase, { rowData }) => (
          <Flex flexDirection="row" gap={2} alignItems="center">
            <Tooltip label={rowData?.check_comment || ''} maxW={300} whiteSpace='pre-wrap'>
              <Flex gap={2}>
                {!!value?.id && <Txt color='gray.500' fontSize='14' maxW={300} whiteSpace='pre-wrap'>{value?.name}</Txt> } 
              </Flex>
            </Tooltip>
            {canCheckStatusEdit && <CheckStatusEditModal id={rowData.id} currentStatus={value} currentComment= {rowData.check_comment} />}
          </Flex>
        ),
      },
    },
    {
      name: 'id',
      label: 'Действия',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (id, { rowData }) => {
          return (
            <Flex gap={2}>
              {/* <StatusEditModal id={id} onClose={onUpdate} /> */}
              {['1', '2', '9'].includes('' + rowData.status.id) && (
                <IconButton
                  onClick={() => history.push('/equipments-repairs/edit/' + id)} aria-label="edit"
                >
                  <MdEdit />
                </IconButton>
              )}
              {['1', '2', '9'].includes('' + rowData.status.id) && (
                <SendModal order={rowData as Partial<EquipmentsRepairFormInstance>} id={id} />
              )}
              {['1', '2', 3, '9'].includes('' + rowData.status.id) && (
                <CancelModal id={id} />
              )}
            </Flex>
          );
        },
      },
    },
  ];

  return columns;
};
